import React, { useEffect, useRef } from 'react';
import { onboardingStatusHint, onboardingStatusLabel } from '../appHelpers';

export function StatusCard({ children, tone = 'default' }) {
  const className = tone === 'error' ? 'status-card error-card' : 'status-card';
  return <section className={className}>{children}</section>;
}

export function CompactMetric({ label, value, tone = 'default' }) {
  const className = ['compact-metric', tone === 'muted' ? 'compact-metric-muted' : ''].filter(Boolean).join(' ');
  return (
    <div className={className}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function StageStatusBadge({ status = 'pending', children }) {
  const label = children || status;
  return <span className={`stage-status-badge stage-status-${status}`}>{label}</span>;
}

export function PipelineStep({ step, index }) {
  return (
    <a
      className={`pipeline-step pipeline-step-${step.status || 'pending'}`}
      href={step.href}
      aria-current={step.current ? 'step' : undefined}
      aria-label={`${step.kicker}: ${step.label}. ${step.statusLabel || step.status || 'pending'}`}
    >
      <span className="pipeline-step-index">{index + 1}</span>
      <span className="pipeline-step-copy">
        <span>{step.kicker}</span>
        <strong>{step.label}</strong>
        {step.meta ? <small>{step.meta}</small> : null}
      </span>
      <StageStatusBadge status={step.status || 'pending'}>{step.statusLabel}</StageStatusBadge>
    </a>
  );
}

export function PipelineStrip({ title = 'Runtime-to-evaluation pipeline', summary, steps, className = '' }) {
  if (!steps?.length) {
    return null;
  }
  return (
    <section className={['panel pipeline-strip', className].filter(Boolean).join(' ')} aria-label={title}>
      <div className="pipeline-strip-header">
        <div>
          <p className="detail-kicker">{title}</p>
          {summary ? <p>{summary}</p> : null}
        </div>
      </div>
      <ol className="pipeline-step-list">
        {steps.map((step, index) => (
          <li key={step.key || step.label}>
            <PipelineStep step={step} index={index} />
          </li>
        ))}
      </ol>
    </section>
  );
}

export function SectionHeader({ kicker, title, copy, action }) {
  return (
    <div className="chapter-header section-header">
      <div>
        {kicker ? <p className="chapter-kicker">{kicker}</p> : null}
        <h3>{title}</h3>
        {copy ? <p className="panel-copy">{copy}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function ActionCluster({ children, align = 'start', className = '' }) {
  return <div className={['action-cluster', `action-cluster-${align}`, className].filter(Boolean).join(' ')}>{children}</div>;
}

export function DisclosurePanel({ title, children, className = '', defaultOpen = false, summaryMeta = null, ...props }) {
  return (
    <details className={['disclosure-panel', className].filter(Boolean).join(' ')} open={defaultOpen || undefined} {...props}>
      <summary>
        <span>{title}</span>
        {summaryMeta ? <small>{summaryMeta}</small> : null}
      </summary>
      <div className="disclosure-panel-body">{children}</div>
    </details>
  );
}

export function HeroMetric({ label, value }) {
  return (
    <div className="hero-metric">
      <span className="hero-metric-value">{value}</span>
      <span className="hero-metric-label">{label}</span>
    </div>
  );
}

export function HeroMetricPlaceholder({ label, value, hint }) {
  return (
    <div className="hero-metric hero-metric-muted">
      <span className="hero-metric-value">{value}</span>
      <span className="hero-metric-label">{label}</span>
      {hint ? <span className="hero-metric-hint">{hint}</span> : null}
    </div>
  );
}

export function DeleteConfirmDialog({ systemName, submitting, errorMessage, onCancel, onConfirm }) {
  const cancelButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);

  useEffect(() => {
    cancelButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!submitting) {
          onCancel();
        }
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = [cancelButtonRef.current, deleteButtonRef.current].filter(Boolean);
      if (!focusable.length) {
        return;
      }
      const activeIndex = focusable.indexOf(document.activeElement);
      if (event.shiftKey) {
        if (activeIndex <= 0) {
          event.preventDefault();
          focusable[focusable.length - 1].focus();
        }
      } else if (activeIndex === focusable.length - 1) {
        event.preventDefault();
        focusable[0].focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel, submitting]);

  return (
    <div
      className="modal-overlay"
      data-testid="delete-dialog-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onCancel();
        }
      }}
    >
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="delete-system-title">
        <p className="detail-kicker">Delete</p>
        <h3 id="delete-system-title">Delete system?</h3>
        <p className="panel-copy">This will remove it and related data.</p>
        <p className="delete-context">
          <strong>System:</strong> {systemName}
        </p>
        {errorMessage ? <p className="delete-error">{errorMessage}</p> : null}
        <div className="form-actions modal-actions">
          <button ref={cancelButtonRef} className="secondary-button" type="button" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button ref={deleteButtonRef} className="danger-button" type="button" onClick={onConfirm} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </section>
    </div>
  );
}

export function OnboardingBanner({ onboarding, stalled, onRefresh }) {
  if (!onboarding) {
    return null;
  }
  const isFailed = onboarding.status === 'failed';
  const statusHint = onboardingStatusHint(onboarding.status, stalled);
  const contextWarning = onboarding.mappingContextWarning?.message;
  const duplicateWarning = onboarding.duplicateContentWarning?.message;
  const graphMode = onboarding.componentGraphContext?.mode;
  const graphLabel =
    graphMode === 'existing_component_graph'
      ? 'Mapped using existing component graph'
      : graphMode === 'clean_new_workflow'
        ? 'Mapped from a clean/new workflow'
        : null;

  return (
    <section className={isFailed ? 'status-card error-card' : 'status-card onboarding-card'}>
      <div className="onboarding-banner">
        <div>
          <p className="card-kicker">Onboarding status</p>
          <strong>{onboardingStatusLabel(onboarding.status)}</strong>
          {onboarding.failureReason ? <p>{onboarding.failureReason}</p> : null}
          {stalled ? <p>Still working. Refresh to check status.</p> : null}
          {statusHint ? <p>{statusHint}</p> : null}
          {graphLabel || contextWarning || duplicateWarning ? (
            <div className="trace-analysis-chip-row" aria-label="Onboarding mapping context">
              {graphLabel ? <span className="chip chip-success">{graphLabel}</span> : null}
              {contextWarning ? <span className="chip chip-warning">{contextWarning}</span> : null}
              {duplicateWarning ? <span className="chip chip-warning">{duplicateWarning}</span> : null}
            </div>
          ) : null}
        </div>
        {stalled ? (
          <button className="secondary-button" type="button" onClick={onRefresh}>
            Refresh status
          </button>
        ) : null}
      </div>
    </section>
  );
}
